/**
 * Finance domain — deposits + withdrawals + operator house banks.
 *
 * POST /deposits body: ManualDepositRequest contract (memberId, amount, bankAccountId, mode?)
 * POST /withdraws body: { memberId, amount, memberBankId }
 *
 * Amount sent to API is in integer minor units (bigint → Number() at boundary).
 */
import { useQuery } from '@tanstack/react-query';
// TYPE-ONLY, and it has to stay that way: these are TypeBox schema objects, and
// a value import would ship the library to a browser that never validates with
// it. `import type` is erased, so the shape crosses and nothing else does.
import type { Static } from '@b1fr0st/contracts/common';
import type { DepositListItem, WithdrawListItem } from '@b1fr0st/contracts/domain/finance-list';
import { apiFetch } from '../../lib/api/client.js';
import { useAuthStore } from '../../stores/auth.js';
import { useBrandingStore } from '../../stores/branding.js';

export const FINANCE_QUERY_KEYS = {
  deposits: (status?: string) => ['finance', 'deposits', status ?? 'all'] as const,
  withdraws: (status?: string) => ['finance', 'withdraws', status ?? 'all'] as const,
  houseBanks: ['finance', 'houseBanks'] as const,
  displayRate: ['finance', 'displayRate'] as const,
  withdrawQuote: (amount: string) => ['finance', 'withdrawQuote', amount] as const,
  transferMinimums: ['finance', 'transferMinimums'] as const,
  bankMasters: (filter: { country?: string; currency?: string }) =>
    ['finance', 'bankMasters', filter] as const,
} as const;

// ── Bank reference (master list per country) ──────────────────────────────────

export interface BankMaster {
  id: string;
  code: string;
  name: string;
  localName: string | null;
  logoUrl: string | null;
  colorHex: string | null;
  country: string;
  currency: string;
  swiftCode: string | null;
  isActive: boolean;
  sortOrder: number;
}

export function fetchBankMasters(filter: {
  country?: string;
  currency?: string;
}): Promise<{ items: BankMaster[] }> {
  const q = new URLSearchParams();
  if (filter.country) q.set('country', filter.country);
  if (filter.currency) q.set('currency', filter.currency);
  const qs = q.toString();
  return apiFetch<{ items: BankMaster[] }>(`/banks${qs ? `?${qs}` : ''}`);
}

export function useBankMasters(filter: { country?: string; currency?: string }) {
  return useQuery({
    queryKey: FINANCE_QUERY_KEYS.bankMasters(filter),
    queryFn: () => fetchBankMasters(filter),
    enabled: Boolean(filter.country || filter.currency),
    staleTime: 60 * 60_000,
  });
}

/**
 * The currency a player's own bank accounts are listed under: the storefront's
 * display currency, which is what svc-member checks a new account against —
 * not the ledger currency `useDisplayMoney` falls back to without a display
 * rate. Undefined until site-config has loaded, so `useBankMasters` waits
 * instead of fetching a guess.
 */
export function useBankCurrency(): string | undefined {
  return useBrandingStore((s) => s.branding?.currency || s.branding?.ledgerCurrency);
}

// ── Operator house banks ───────────────────────────────────────────────────────

export interface HouseBank {
  id: string;
  /**
   * 'bank' | 'promptpay'. A row saved before the column existed comes back
   * 'bank' from the database default. It decides the label, the icon and which
   * kind of number the copy control says it is copying — not whether a QR is
   * drawn. `showQr` decides that, on either kind.
   */
  accountType: string;
  bankCode: string;
  accountNumber: string;
  accountName: string;
  usage: string;
  /**
   * Which deposit flow this account is offered on: 'any' (every flow) or one
   * of the DEPOSIT_MODES ids. `banksForMode` applies it.
   *
   * Optional on the wire for the same reason as `showQr` below: this SPA can be
   * newer than svc-finance, and a reply written before the column existed
   * carries none. Absent reads as 'any', which is the column's own default and
   * what every account did before the flows could be split.
   */
  depositChannel?: string;
  displayOrder: number;
  /** EMVCo/PromptPay string to render as a QR. Null when the operator set none. */
  qrPayload: string | null;
  /**
   * The operator's per-account switch for the QR.
   *
   * Optional on the wire on purpose. This SPA ships from Cloudflare Pages on its
   * own cadence, so it can be newer than svc-finance, and a strict read would
   * take the QR off every account for the length of that window — the exact
   * thing the column's `DEFAULT true` exists to prevent. Absent therefore reads
   * as ON, which is what every account did before the switch existed.
   */
  showQr?: boolean;
  /** Minor units. Null = the operator set no floor for this account. */
  minDeposit: number | null;
  /** The currency this account receives in. Null or absent = the ledger currency. */
  currency?: string | null;
  /** Account-currency units per ONE ledger unit (`'700'`). Null = no conversion. */
  convertRate?: string | null;
}

export function fetchHouseBanks(): Promise<{ banks: HouseBank[] }> {
  return apiFetch<{ banks: HouseBank[] }>('/operator/banks');
}

export function useHouseBanks() {
  return useQuery({
    queryKey: FINANCE_QUERY_KEYS.houseBanks,
    queryFn: fetchHouseBanks,
    staleTime: 5 * 60_000,
  });
}

// ── Display rate ──────────────────────────────────────────────────────────────

/**
 * The rate the site shows ledger money at. `rate` = display units per ONE ledger
 * unit, null when the currencies match or no rate is configured. Public — no
 * login needed, the brand comes from the host.
 */
export interface DisplayRate {
  displayCurrency: string;
  ledgerCurrency: string;
  rate: string | null;
  source: 'brand' | 'reference' | 'identity' | null;
}

export function fetchDisplayRate(): Promise<DisplayRate> {
  return apiFetch<DisplayRate>('/display-rates/current');
}

export function useDisplayRate() {
  return useQuery({
    queryKey: FINANCE_QUERY_KEYS.displayRate,
    queryFn: fetchDisplayRate,
    staleTime: 10 * 60_000,
  });
}

// ── Transfer minimums ─────────────────────────────────────────────────────────

/**
 * The agent's configured deposit/withdraw floors, in minor units. `null` is the
 * answer when the operator set no floor on any account that could take the
 * transfer AND no platform default behind them — the create paths accept any
 * amount then, so a screen must show no minimum rather than substitute one of
 * its own.
 *
 * Served by the same resolver svc-finance refuses FIN-306/FIN-307 against, so
 * the figure a player reads and the figure they are held to are one value.
 */
export interface TransferMinimums {
  minDeposit: number | null;
  minWithdraw: number | null;
}

export function fetchTransferMinimums(): Promise<TransferMinimums> {
  return apiFetch<TransferMinimums>('/operator/banks/minimums');
}

export function useTransferMinimums() {
  return useQuery({
    queryKey: FINANCE_QUERY_KEYS.transferMinimums,
    queryFn: fetchTransferMinimums,
    staleTime: 5 * 60_000,
  });
}

// ── Deposit ───────────────────────────────────────────────────────────────────

/**
 * A `finance.deposits` row, taken FROM the schema svc-finance serialises it
 * with rather than re-typed here.
 *
 * The names below are checked against `DepositListItem`: rename a field on the
 * server and this `Pick` stops compiling. Hand-written, the same rename was
 * silent — the screen read a key that no longer existed and rendered a blank,
 * more than once. That is the whole point of the
 * indirection; keep it.
 *
 * A `Pick` and not the whole `Static<typeof DepositListItem>`, for two reasons.
 * The list carries operator-only columns (`createdBy`, `txId`, `balanceBefore`,
 * the house-account trio) that no player screen reads and no fixture should
 * have to spell. And this same type is what `POST /deposits` and the cancel
 * route answer with, which is the open `DepositRow` — every name picked here is
 * a row field, so all three replies satisfy it.
 *
 * `status` is a bare string because the wire says so (`t.String()`), not an
 * oversight: the column has no enum behind it, and the values it carries are
 * documented on the schema. `depositStatusToDisplay` maps them, and a value it
 * does not know reads as "still being processed" rather than failing to
 * compile somewhere far away.
 *
 * `currency`, `note` and `bankName` are absent because they are absent from the
 * wire — the first two have no column, and the list schema is CLOSED so a name
 * it does not carry is stripped before it reaches here. Currency comes from the
 * screen's own `useMoneyCurrency()`. `slipRef` is a `finance.slip_uploads.id`,
 * opaque and never a URL — only the operator slip-view route resolves it.
 */
export type Deposit = Pick<
  Static<typeof DepositListItem>,
  | 'id'
  | 'memberId'
  | 'agentId'
  | 'amount'
  | 'status'
  | 'channel'
  | 'houseBankAccountId'
  | 'memberBankAccountId'
  | 'slipRef'
  | 'approvedAt'
  | 'rejectReason'
  | 'createdAt'
  | 'updatedAt'
  | 'statementId'
  | 'originalAmount'
  | 'originalCurrency'
  | 'convertRate'
>;

export interface DepositPage {
  items: Deposit[];
  total: number;
  page: number;
  pageSize: number;
}

export function fetchDeposits(params?: {
  status?: string;
  pageSize?: number;
  page?: number;
}): Promise<DepositPage> {
  const qs = new URLSearchParams();
  const memberId = useAuthStore.getState().subject?.id;
  if (memberId) qs.set('memberId', memberId);
  if (params?.status) qs.set('status', params.status);
  qs.set('pageSize', String(params?.pageSize ?? 20));
  if (params?.page) qs.set('page', String(params.page));
  return apiFetch<DepositPage>(`/deposits?${qs.toString()}`);
}

export interface CreateDepositArgs {
  memberId: string;
  /**
   * The member's OWN bank account the transfer is sent from — verified or not,
   * since a deposit needs no operator verification. Not a house rail:
   * svc-finance stores it as `memberBankAccountId`, and the house account the
   * money landed in is named by the approving operator.
   */
  bankAccountId: string;
  /** Ledger minor units. Beside `originalAmount` it is only an estimate the server recomputes. */
  amount: number;
  /** Minor units of `transferAccountId`'s own currency, exactly what the player sends. */
  originalAmount?: number;
  transferAccountId?: string;
  mode?: 'copy' | 'qr' | 'slip' | 'gateway';
  /**
   * A `finance.slip_uploads.id` from `uploadDepositSlip` — opaque, never a URL.
   * svc-finance looks it up and refuses one that is not the caller's own
   * (FIN-312); only meaningful when `mode` is `'slip'`.
   */
  slipRef?: string;
}

export function createDeposit(args: CreateDepositArgs): Promise<Deposit> {
  return apiFetch<Deposit>('/deposits', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ ...args, mode: args.mode ?? 'copy' }),
  });
}

// ── Deposit slip upload (player self-service) ──────────────────────────────────

export interface SlipUploadResult {
  /** `finance.slip_uploads.id` — pass this back as `slipRef` on `createDeposit`. */
  slipRef: string;
}

/**
 * Uploads a deposit slip photo/PDF to private storage and hands back an opaque
 * reference — never a URL, never a storage key. `POST /deposit-slips` is a
 * TOP-LEVEL gateway resource (not nested under `/deposits`) so it can carry its
 * own tighter rate limit; the nested path is closed and answers 404.
 */
export function uploadDepositSlip(file: File): Promise<SlipUploadResult> {
  const form = new FormData();
  form.append('file', file);
  return apiFetch<SlipUploadResult>('/deposit-slips', {
    method: 'POST',
    body: form,
    // Do NOT set content-type — the browser sets it with the multipart boundary.
  });
}

/**
 * Withdraw a deposit request the member filed and no operator has settled yet.
 *
 * The member id is NOT a parameter: svc-finance authorizes this one off the
 * token and answers a row belonging to anyone else with a 404, so there is
 * nothing here for a caller to point at another member's request.
 */
export function cancelDeposit(depositId: string): Promise<Deposit> {
  return apiFetch<Deposit>(`/deposits/${depositId}/cancel`, { method: 'POST' });
}

/** The two statuses svc-finance will still accept a cancel for. */
export function isCancellableDeposit(status: string): boolean {
  return status === 'pending' || status === 'verifying';
}

/**
 * Whether the request is still waiting on the player's money.
 *
 * The same two statuses as `isCancellableDeposit`, asked as a different
 * question: `approved` is paid, `rejected` and `cancelled` are dead, and none
 * of the three has transfer instructions left to show. Kept as its own
 * predicate so a later change to one answer cannot silently move the other —
 * an operator flow that stops accepting a cancel says nothing about whether
 * the player may still pay.
 */
export function isResumableDeposit(status: string): boolean {
  return status === 'pending' || status === 'verifying';
}

// ── Withdraw ──────────────────────────────────────────────────────────────────

/**
 * A `GET /withdraws` row, taken FROM the CLOSED `WithdrawListItem` schema
 * rather than re-typed here. Closed means a name absent there is stripped
 * before it reaches this app — so a field this type could once name and the
 * server never sent read `undefined` forever, and now does not compile.
 *
 * Five such names used to sit in the hand-written version and every one was a
 * silent blank: `rejectedReason` (the wire and the column both say
 * `rejectReason`, so the operator's reason was read off a key that does not
 * exist), `rejectedAt` and `currency` (no such columns — currency comes from
 * the screen's own `useMoneyCurrency()`), `note` (see the deposit twin), and
 * `completedAt` (the settlement timestamp is `paidAt`).
 *
 * `status` is whatever `finance.withdraws.status` holds. The hand-written union
 * claimed four values and the column has six: `'paid'` was never one of them —
 * svc-finance writes `'completed'` — while `debit_failed` and `payout_failed`
 * were missing, which is how a rejected withdrawal came to render as "pending".
 * Deriving it removes the chance to be wrong about that again;
 * `withdrawStatusToDisplay` is where the values are mapped.
 *
 * The `Pick` is deliberate — see the deposit twin for why the operator-only
 * columns stay out, and why `POST /withdraws` can answer with this same type.
 * `feeAmount` is never null on the wire (NOT NULL, zero default), and `paidAt`
 * is when the payout was marked sent.
 */
export type Withdraw = Pick<
  Static<typeof WithdrawListItem>,
  | 'id'
  | 'memberId'
  | 'agentId'
  | 'amount'
  | 'status'
  | 'memberBankAccountId'
  | 'feeAmount'
  | 'approvedAt'
  | 'paidAt'
  | 'rejectReason'
  | 'createdAt'
  | 'updatedAt'
  | 'payoutAmount'
  | 'payoutCurrency'
  | 'convertRate'
>;

export interface WithdrawPage {
  items: Withdraw[];
  total: number;
  page: number;
  pageSize: number;
}

export function fetchWithdraws(params?: {
  status?: string;
  pageSize?: number;
  page?: number;
}): Promise<WithdrawPage> {
  const qs = new URLSearchParams();
  const memberId = useAuthStore.getState().subject?.id;
  if (memberId) qs.set('memberId', memberId);
  if (params?.status) qs.set('status', params.status);
  qs.set('pageSize', String(params?.pageSize ?? 20));
  if (params?.page) qs.set('page', String(params.page));
  return apiFetch<WithdrawPage>(`/withdraws?${qs.toString()}`);
}

export interface CreateWithdrawArgs {
  memberId: string;
  amount: number;
  memberBankId: string;
}

export function createWithdraw(args: CreateWithdrawArgs): Promise<Withdraw> {
  return apiFetch<Withdraw>('/withdraws', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(args),
  });
}

/**
 * What a withdrawal of `amount` LEDGER minor units pays out. `payoutAmount` is
 * minor units of `payoutCurrency`; both null = paid in the ledger currency.
 * Refused with FIN-370 (no account can pay this player), FIN-371 (the amount
 * converts to nothing), FIN-383 (the paying account's rate is unusable) or
 * FIN-386 (the payout is too large to record).
 */
export interface WithdrawQuote {
  amount: number;
  payoutAmount: string | null;
  payoutCurrency: string | null;
  rate: string | null;
}

export function fetchWithdrawQuote(amountMinor: bigint): Promise<WithdrawQuote> {
  return apiFetch<WithdrawQuote>(`/withdraws/quote?amount=${amountMinor.toString()}`);
}
